import { useState, useEffect, useRef } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Label } from "./components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import {
  ThermometerSnowflake,
  ShieldCheck,
  Cctv,
  Truck,
  MapPin,
  Phone,
  Mail,
  Menu,
  X,
  Clock,
  FileText,
  BarChart3,
  Wallet,
  Package,
  Users,
  MessageSquare,
  Eye,
  CheckCircle2,
  ArrowRight,
  Star,
  ChevronRight,
  Building2,
  ClipboardCheck,
  Boxes,
  BadgeCheck,
} from "lucide-react";

const FORMSPREE_ENDPOINT = "https://formspree.io/f/mlgwagda";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_climate-vault/artifacts/zn5m3r67_SK%20COLD%20STORAGE%20LOGO%20FINAL%2001%20Artboard%201.JPG";
const HERO_BG = "https://images.pexels.com/photos/12706241/pexels-photo-12706241.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const WAREHOUSE_IMG = "https://images.unsplash.com/photo-1768796372610-f844d490a734?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHw0fHxpbmR1c3RyaWFsJTIwd2FyZWhvdXNlJTIwc3RvcmFnZSUyMHNoZWx2ZXMlMjBwYWxsZXRzJTIwbG9naXN0aWNzfGVufDB8fHx8MTc3MTkxOTc5Mnww&ixlib=rb-4.1.0&q=85";

// Ranjangaon MIDC, Pune coordinates
const MAP_EMBED_URL = "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3781.123!2d74.1344!3d18.5088!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc2c1e1e1e1e1e1%3A0x1234567890abcdef!2sRanjangaon%20MIDC%2C%20Pune!5e0!3m2!1sen!2sin!4v1";

const CONTACT_INFO = {
  phone: "+919527005006",
  whatsapp: "+919527005006",
  emails: ["shreyashkale@skcoldstorage.in", "hanumant.tambe@skcoldstorage.in"],
  address: "Plot No. E1, Ranjangaon MIDC, Pune-Nagar Highway, Taluka Shirur, Pune – 412209",
  mapCoords: { lat: 18.8522, lng: 74.1344 }
};

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Navigation Component
const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Services", href: "#capabilities" },
    { name: "Process", href: "#process" },
    { name: "Contact", href: "#contact" }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "glass-header bg-white/90 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-3" data-testid="logo-link">
            <img
              src={LOGO_URL}
              alt="SK Cold Storage Logo"
              className="h-10 md:h-12 w-auto object-contain"
            />
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <button
                key={link.name}
                onClick={() => scrollToSection(link.href)}
                className={`nav-link text-sm font-medium transition-colors ${
                  isScrolled ? "text-slate-700 hover:text-slate-900" : "text-white hover:text-white/80"
                }`}
                data-testid={`nav-${link.name.toLowerCase()}`}
              >
                {link.name}
              </button>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              onClick={() => scrollToSection("#contact")}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-md px-5 py-2 font-medium transition-all"
              data-testid="header-cta"
            >
              Get Quote
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className={isScrolled ? "text-slate-900" : "text-white"} size={24} />
            ) : (
              <Menu className={isScrolled ? "text-slate-900" : "text-white"} size={24} />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100"
          >
            <div className="px-4 py-4 space-y-3">
              {navLinks.map((link) => (
                <button
                  key={link.name}
                  onClick={() => scrollToSection(link.href)}
                  className="block w-full text-left py-2 text-slate-700 font-medium hover:text-emerald-700"
                  data-testid={`mobile-nav-${link.name.toLowerCase()}`}
                >
                  {link.name}
                </button>
              ))}
              <Button
                onClick={() => scrollToSection("#contact")}
                className="w-full bg-emerald-700 hover:bg-emerald-800 text-white mt-4"
                data-testid="mobile-cta"
              >
                Get Quote
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src={HERO_BG}
          alt="Cold storage facility"
          className="w-full h-full object-cover"
        />
        <div className="hero-overlay absolute inset-0" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 md:py-40">
        <motion.div
          initial="initial"
          animate="animate"
          variants={staggerContainer}
          className="max-w-3xl"
        >
          <motion.h1
            variants={fadeInUp}
            className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-tight"
          >
            Reliable Temperature-Controlled Storage.
          </motion.h1>
          <motion.p
            variants={fadeInUp}
            className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed max-w-2xl"
          >
            Professionally managed storage solutions built on transparency, structure, and operational discipline.
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            variants={fadeInUp}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <Button
              onClick={scrollToContact}
              size="lg"
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-6 text-base rounded-md transition-all"
              data-testid="hero-cta-primary"
            >
              Request Storage Space
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              onClick={scrollToContact}
              size="lg"
              variant="outline"
              className="bg-white/10 hover:bg-white/20 text-white border-white/30 font-semibold px-8 py-6 text-base rounded-md backdrop-blur-sm"
              data-testid="hero-cta-secondary"
            >
              Schedule a Visit
            </Button>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            variants={fadeInUp}
            className="mt-10 flex flex-wrap gap-3"
          >
            {[
              { icon: ThermometerSnowflake, text: "Temperature Controlled" },
              { icon: Eye, text: "24/7 Monitoring" },
              { icon: Wallet, text: "Transparent Billing" },
              { icon: ShieldCheck, text: "Secure Facility" }
            ].map((badge, idx) => (
              <span key={idx} className="trust-badge" data-testid={`trust-badge-${idx}`}>
                <badge.icon size={14} />
                {badge.text}
              </span>
            ))}
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center p-1">
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 1.5, repeat: Infinity }}
            className="w-1.5 h-1.5 bg-white rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const features = [
    "Modern temperature-controlled chambers",
    "Structured intake and documentation process",
    "Flexible seasonal & monthly contracts",
    "Refundable security deposits",
    "Advance billing clarity",
    "Multi-product storage flexibility",
    "Continuous monitoring including CCTV & temperature logs"
  ];

  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-36 bg-white relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-20 right-20 w-72 h-72 bg-emerald-100 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-sky-100 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl professional-image">
              <img
                src={WAREHOUSE_IMG}
                alt="SK Cold Storage facility"
                className="w-full h-[400px] md:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/30 to-transparent" />
            </div>
            {/* Stats overlay */}
            <div className="absolute -bottom-6 -right-6 stats-badge text-white p-6 rounded-2xl shadow-xl hidden md:block">
              <div className="text-4xl font-bold">100%</div>
              <div className="text-sm text-emerald-100 mt-1">Uptime Commitment</div>
            </div>
            {/* Decorative element */}
            <div className="absolute -top-4 -left-4 w-24 h-24 border-2 border-emerald-200 rounded-2xl hidden lg:block" />
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
              About SK Cold Storage
            </span>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
              Built on Operational Discipline.
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              At SK Cold Storage, we combine modern infrastructure with systematic processes to deliver reliable temperature-controlled storage solutions. Located in Ranjangaon MIDC, we serve businesses across Pune and Maharashtra with professionalism and transparency.
            </p>

            <ul className="mt-8 space-y-3">
              {features.map((feature, idx) => (
                <motion.li
                  key={idx}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="h-5 w-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span className="text-slate-700">{feature}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Capabilities Section
const CapabilitiesSection = () => {
  const capabilities = [
    {
      icon: Package,
      title: "Multi-Product Storage",
      description: "Flexible storage solutions accommodating diverse product categories with appropriate handling protocols."
    },
    {
      icon: FileText,
      title: "Flexible Contract Options",
      description: "Choose from seasonal, monthly, or customized contract terms that suit your business needs."
    },
    {
      icon: BarChart3,
      title: "Daily Temperature Logging",
      description: "Multiple daily temperature readings with documented logs for complete operational transparency."
    },
    {
      icon: ClipboardCheck,
      title: "Damage Tracking & Reporting",
      description: "Systematic documentation of product condition with detailed reporting mechanisms."
    },
    {
      icon: Wallet,
      title: "Transparent Cost Structure",
      description: "Clear, upfront pricing with no hidden charges. Electricity billing based on actual usage."
    },
    {
      icon: Users,
      title: "Professional Handling",
      description: "Trained workforce with per MT labour model ensuring careful handling of your products."
    },
    {
      icon: MessageSquare,
      title: "WhatsApp Operational Alerts",
      description: "Real-time updates and notifications delivered directly to your phone for seamless communication."
    },
    {
      icon: Eye,
      title: "Continuous Facility Monitoring",
      description: "Round-the-clock surveillance including CCTV and regular security patrols."
    }
  ];

  return (
    <section id="capabilities" data-testid="capabilities-section" className="py-24 md:py-36 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <span className="inline-block px-4 py-1.5 bg-emerald-50 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
            Our Capabilities
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Comprehensive Storage Solutions
          </h2>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            From intake to dispatch, we provide end-to-end cold storage services with a focus on reliability and transparency.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {capabilities.map((capability, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="group capability-card"
              data-testid={`capability-${idx}`}
            >
              <div className="icon-container mb-5 group-hover:scale-110 transition-transform duration-300">
                <capability.icon size={24} />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                {capability.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {capability.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Process Section
const ProcessSection = () => {
  const steps = [
    {
      number: "01",
      title: "Inquiry & Space Allocation",
      description: "Share your storage requirements and we'll allocate appropriate space based on your needs."
    },
    {
      number: "02",
      title: "Intake & Documentation",
      description: "Structured onboarding with thorough documentation of your products and requirements."
    },
    {
      number: "03",
      title: "Temperature-Controlled Storage",
      description: "Your products are stored in optimally controlled environments with regular monitoring."
    },
    {
      number: "04",
      title: "Regular Monitoring & Logs",
      description: "Daily temperature logs and systematic checks ensure product integrity."
    },
    {
      number: "05",
      title: "Transparent Monthly Billing",
      description: "Clear, itemized invoices with no hidden costs. Pay only for what you use."
    },
    {
      number: "06",
      title: "Secure Dispatch",
      description: "Professional handling during dispatch with complete documentation and verification."
    }
  ];

  return (
    <section id="process" data-testid="process-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
            How It Works
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Simple, Structured Process
          </h2>
          <p className="mt-6 text-lg text-slate-600">
            From inquiry to dispatch, every step is designed for efficiency and transparency.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {steps.map((step, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="relative"
              data-testid={`process-step-${idx}`}
            >
              <div className="flex items-start gap-4">
                <div className="step-number flex-shrink-0">{step.number}</div>
                <div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Why Choose Us Section
const WhyChooseSection = () => {
  const reasons = [
    {
      icon: BadgeCheck,
      title: "Refundable Deposits",
      description: "Your security deposit is fully refundable upon contract completion."
    },
    {
      icon: Wallet,
      title: "No Hidden Electricity Billing",
      description: "Transparent electricity charges based on actual consumption."
    },
    {
      icon: BarChart3,
      title: "Multiple Daily Temperature Logs",
      description: "Regular monitoring with documented temperature readings."
    },
    {
      icon: Users,
      title: "Clear Labour Charge Model",
      description: "Per MT labour charges with no surprise fees."
    },
    {
      icon: Boxes,
      title: "Multi-Product Flexibility",
      description: "Store diverse product categories with appropriate protocols."
    },
    {
      icon: FileText,
      title: "Structured Documentation",
      description: "Complete paperwork for every transaction and movement."
    }
  ];

  return (
    <section data-testid="why-choose-section" className="py-20 md:py-32 bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-400">
            Why Choose Us
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight">
            Why Choose SK Cold Storage?
          </h2>
          <p className="mt-6 text-lg text-slate-300">
            We prioritize transparency, reliability, and professional service in everything we do.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {reasons.map((reason, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="bg-slate-800/50 backdrop-blur-sm p-6 rounded-xl border border-slate-700 hover:border-emerald-500/50 transition-colors"
              data-testid={`why-choose-${idx}`}
            >
              <div className="icon-container-emerald mb-4">
                <reason.icon size={24} />
              </div>
              <h3 className="text-lg font-semibold mb-2">{reason.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">
                {reason.description}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  const testimonials = [
    {
      name: "Rajesh Kumar",
      company: "Fresh Foods Pvt. Ltd.",
      content: "SK Cold Storage has been instrumental in maintaining our supply chain integrity. Their transparent billing and professional handling give us peace of mind.",
      rating: 5
    },
    {
      name: "Priya Sharma",
      company: "MedPharma Solutions",
      content: "The temperature control and monitoring systems are top-notch. We've been storing our temperature-sensitive products here for over a year without any issues.",
      rating: 5
    },
    {
      name: "Amit Patel",
      company: "Agri Export Co.",
      content: "Flexible contracts and refundable deposits made it easy for us to start. The WhatsApp updates keep us informed about our inventory status.",
      rating: 5
    }
  ];

  return (
    <section data-testid="testimonials-section" className="py-20 md:py-32 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
            Testimonials
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Trusted by Businesses
          </h2>
          <p className="mt-6 text-lg text-slate-600">
            Hear what our clients have to say about their experience with SK Cold Storage.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="testimonial-card"
              data-testid={`testimonial-${idx}`}
            >
              <div className="flex gap-1 mb-4">
                {[...Array(testimonial.rating)].map((_, i) => (
                  <Star key={i} size={16} className="fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-slate-600 leading-relaxed mb-6 relative z-10">
                {testimonial.content}
              </p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                  <span className="text-emerald-700 font-semibold text-sm">
                    {testimonial.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="font-semibold text-slate-900 text-sm">{testimonial.name}</div>
                  <div className="text-slate-500 text-xs">{testimonial.company}</div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const scrollToContact = () => {
    document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section data-testid="cta-section" className="py-24 md:py-36 cta-gradient relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-full">
        <div className="absolute top-10 left-10 w-32 h-32 border border-white/10 rounded-full" />
        <div className="absolute bottom-10 right-10 w-48 h-48 border border-white/10 rounded-full" />
        <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-white/5 rounded-full blur-3xl" />
      </div>
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white tracking-tight">
            Storage You Can Depend On.
          </h2>
          <p className="mt-6 text-lg text-emerald-100/90 max-w-2xl mx-auto leading-relaxed">
            Ready to optimize your cold chain? Get in touch with us today and experience the SK Cold Storage difference.
          </p>
          <Button
            onClick={scrollToContact}
            size="lg"
            className="mt-10 bg-white text-emerald-700 hover:bg-emerald-50 font-semibold px-10 py-6 text-base rounded-full shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1"
            data-testid="cta-button"
          >
            Reserve Capacity
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    phone: "",
    storageRequirement: "",
    quantity: "",
    duration: "",
    temperatureRequirement: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          storage_requirement: formData.storageRequirement,
          quantity_mt: formData.quantity,
          duration: formData.duration,
          temperature_requirement: formData.temperatureRequirement,
          _subject: `New Inquiry from ${formData.name} - SK Cold Storage`
        })
      });

      const data = await response.json();
      
      if (response.ok && data.ok) {
        toast.success("Thank you for your inquiry!", {
          description: "Our team will contact you within 24 hours."
        });

        setFormData({
          name: "",
          company: "",
          phone: "",
          storageRequirement: "",
          quantity: "",
          duration: "",
          temperatureRequirement: ""
        });
      } else {
        throw new Error(data.error || "Form submission failed");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Something went wrong", {
        description: "Please try again or contact us directly at +919527005006"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-xs font-bold uppercase tracking-widest text-emerald-700">
            Get In Touch
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 tracking-tight">
            Contact Us
          </h2>
          <p className="mt-6 text-lg text-slate-600">
            Have questions or ready to reserve storage space? We're here to help.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info & Map */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <div className="space-y-5 mb-8">
              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center flex-shrink-0 shadow-md">
                  <Phone size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Phone</div>
                  <a
                    href={`tel:${CONTACT_INFO.phone}`}
                    className="text-slate-600 hover:text-emerald-700 transition-colors"
                    data-testid="contact-phone"
                  >
                    {CONTACT_INFO.phone}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center flex-shrink-0 shadow-md">
                  <MessageSquare size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">WhatsApp</div>
                  <a
                    href={`https://wa.me/${CONTACT_INFO.whatsapp.replace(/\+/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-slate-600 hover:text-emerald-700 transition-colors"
                    data-testid="contact-whatsapp"
                  >
                    {CONTACT_INFO.whatsapp}
                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center flex-shrink-0 shadow-md">
                  <Mail size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Email</div>
                  <div className="space-y-1">
                    {CONTACT_INFO.emails.map((email, idx) => (
                      <a
                        key={idx}
                        href={`mailto:${email}`}
                        className="block text-slate-600 hover:text-emerald-700 transition-colors text-sm"
                        data-testid={`contact-email-${idx}`}
                      >
                        {email}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-emerald-700 flex items-center justify-center flex-shrink-0 shadow-md">
                  <MapPin size={20} className="text-white" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">Address</div>
                  <p className="text-slate-600 text-sm">{CONTACT_INFO.address}</p>
                </div>
              </div>
            </div>

            {/* Map with exact pin */}
            <div className="relative map-container shadow-lg" data-testid="contact-map">
              <iframe
                src="https://www.google.com/maps?q=18.787317,74.289452&z=17&output=embed"
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="SK Cold Storage Location - Plot No. E1, Ranjangaon MIDC"
                className="w-full h-full"
              />
              {/* Custom label overlay */}
              <div className="absolute top-4 left-4 bg-white px-4 py-2 rounded-lg shadow-lg border border-slate-200 z-10">
                <div className="flex items-center gap-2">
                  <MapPin size={18} className="text-red-500" />
                  <div>
                    <p className="font-semibold text-slate-900 text-sm">SK Cold Storage</p>
                    <p className="text-xs text-slate-500">Ranjangaon MIDC, Pune</p>
                  </div>
                </div>
              </div>
            </div>
            {/* View on larger map link */}
            <a
              href="https://www.google.com/maps?q=18.787317,74.289452&z=17"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-4 text-emerald-700 hover:text-emerald-800 font-medium text-sm transition-colors"
              data-testid="view-larger-map"
            >
              <MapPin size={16} />
              View on larger map
              <ArrowRight size={14} />
            </a>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <form
              onSubmit={handleSubmit}
              className="bg-slate-50 p-8 rounded-xl"
              data-testid="contact-form"
            >
              <h3 className="text-xl font-semibold text-slate-900 mb-6">
                Request Storage Space
              </h3>

              <div className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="name" className="text-slate-700">
                      Name *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-1 form-input"
                      placeholder="Your name"
                      data-testid="form-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="text-slate-700">
                      Company
                    </Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-1 form-input"
                      placeholder="Company name"
                      data-testid="form-company"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="text-slate-700">
                    Phone *
                  </Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="mt-1 form-input"
                    placeholder="+91 XXXXX XXXXX"
                    data-testid="form-phone"
                  />
                </div>

                <div>
                  <Label htmlFor="storageRequirement" className="text-slate-700">
                    Storage Requirement
                  </Label>
                  <Textarea
                    id="storageRequirement"
                    name="storageRequirement"
                    value={formData.storageRequirement}
                    onChange={handleChange}
                    className="mt-1 form-input min-h-[100px]"
                    placeholder="Describe your storage requirements..."
                    data-testid="form-requirement"
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <Label htmlFor="quantity" className="text-slate-700">
                      Quantity (MT)
                    </Label>
                    <Input
                      id="quantity"
                      name="quantity"
                      type="number"
                      value={formData.quantity}
                      onChange={handleChange}
                      className="mt-1 form-input"
                      placeholder="e.g., 100"
                      data-testid="form-quantity"
                    />
                  </div>
                  <div>
                    <Label htmlFor="duration" className="text-slate-700">
                      Duration
                    </Label>
                    <Select
                      value={formData.duration}
                      onValueChange={(value) => handleSelectChange("duration", value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="form-duration">
                        <SelectValue placeholder="Select duration" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1-month">1 Month</SelectItem>
                        <SelectItem value="3-months">3 Months</SelectItem>
                        <SelectItem value="6-months">6 Months</SelectItem>
                        <SelectItem value="1-year">1 Year</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="temperatureRequirement" className="text-slate-700">
                    Temperature Requirement
                  </Label>
                  <Select
                    value={formData.temperatureRequirement}
                    onValueChange={(value) => handleSelectChange("temperatureRequirement", value)}
                  >
                    <SelectTrigger className="mt-1" data-testid="form-temperature">
                      <SelectValue placeholder="Select temperature range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="frozen">Below -18°C (Frozen)</SelectItem>
                      <SelectItem value="chilled">2°C to 8°C (Chilled)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-slate-900 hover:bg-slate-800 text-white font-semibold py-6"
                  disabled={isSubmitting}
                  data-testid="form-submit"
                >
                  {isSubmitting ? "Submitting..." : "Submit Inquiry"}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "Home", href: "#home" },
    { name: "About", href: "#about" },
    { name: "Services", href: "#capabilities" },
    { name: "Process", href: "#process" },
    { name: "Contact", href: "#contact" }
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <footer data-testid="footer" className="bg-slate-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src={LOGO_URL}
              alt="SK Cold Storage"
              className="h-12 w-auto mb-4 brightness-0 invert"
            />
            <p className="text-slate-400 max-w-sm leading-relaxed">
              Professional temperature-controlled storage solutions built on transparency, structure, and operational discipline.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <button
                    onClick={() => scrollToSection(link.href)}
                    className="footer-link hover:text-emerald-400 transition-colors text-sm"
                  >
                    {link.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-2">
                <Phone size={14} />
                <a href={`tel:${CONTACT_INFO.phone}`} className="hover:text-emerald-400 transition-colors">
                  {CONTACT_INFO.phone}
                </a>
              </li>
              {CONTACT_INFO.emails.map((email, idx) => (
                <li key={idx} className="flex items-center gap-2">
                  <Mail size={14} />
                  <a href={`mailto:${email}`} className="hover:text-emerald-400 transition-colors">
                    {email}
                  </a>
                </li>
              ))}
              <li className="flex items-start gap-2">
                <MapPin size={14} className="flex-shrink-0 mt-1" />
                <span>{CONTACT_INFO.address}</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm">
              © {currentYear} SK Cold Storage. All rights reserved.
            </p>
            <div className="flex items-center gap-6 text-sm text-slate-500">
              <span>Professional Cold Storage Solutions</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const phoneNumber = "919527005006";
  const message = encodeURIComponent("Hello, I'm interested in your cold storage services.");
  const whatsappLink = `https://wa.me/${phoneNumber}?text=${message}`;

  const handleClick = (e) => {
    e.preventDefault();
    window.open(whatsappLink, '_blank', 'noopener,noreferrer');
  };

  return (
    <button
      onClick={handleClick}
      className="fixed bottom-24 right-6 z-50 flex items-center justify-center w-14 h-14 bg-[#25D366] text-white rounded-full shadow-lg hover:bg-[#20BD5A] hover:scale-110 transition-all duration-300 whatsapp-pulse cursor-pointer"
      data-testid="whatsapp-button"
      aria-label="Chat on WhatsApp"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="currentColor"
        className="w-7 h-7"
      >
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      </svg>
    </button>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <Toaster position="top-center" richColors />
      <Header />
      <main>
        <HeroSection />
        <AboutSection />
        <CapabilitiesSection />
        <ProcessSection />
        <WhyChooseSection />
        <TestimonialsSection />
        <CTASection />
        <ContactSection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
